import { routerMiddleware } from 'connected-react-router/immutable';
import Immutable from 'immutable';
import { applyMiddleware } from 'redux';
import { createLogger } from 'redux-logger';
import MixpanelMiddleware from 'redux-mixpanel-middleware';
import thunk from 'redux-thunk';
import createEmbedPreviewSync, {
  isAutosaveDriven,
} from 'redux/middleware/embed-preview-sync';
import persistPurge from 'redux/middleware/persist-purge';
import { AUTOSAVE_INTERVAL_MILLIS } from 'utils/constants';

import { isReportableError } from 'utils/sentry';
import createApi from '../middleware/api';
import createAutosave, { isDelayed, isImmediate } from '../middleware/autosave';
import delay from '../middleware/delay';
import createSentry from '../middleware/sentry';
import {
  logout,
  tokenSelector,
  userIdSelector,
  userRegisteredSelector,
} from '../modules/auth';
import { EMBED_CONFIGURATION_CREATE_REQUEST } from '../modules/embed/action-types';
import { doSaveConfiguration } from '../modules/embed/actions/embed';
import modalMiddleware from '../modules/modal/middleware';
import history from './history';

const router = routerMiddleware(history);

const autosave = createAutosave(doSaveConfiguration, AUTOSAVE_INTERVAL_MILLIS);

const embedPreviewSync = createEmbedPreviewSync({
  triggerAction: action => {
    // this catches all autosave actions.  the sync is throttled and is only called
    // once it stops receiving trigger actions, so it's ok if this fires often
    if (isImmediate(action) || isDelayed(action)) {
      return true;
    }

    // when assets are added via modals in the AE, they circumvent the autosave
    // middleware and dispatch the call to create the embec config directly.  These
    // actions won't be caught by the autosave middleware condition above, so we listen
    // for the action to create the embed config.
    //
    // autosave events also update the embed config.  First, we'll match on the
    // autosave middleware action types and sync the preview, then, we'll see an
    // embed config create action _with the exact same payload_.
    //
    // To see how we avoid dispatching multiple sync events with the same payload,
    // see the embed-preview-sync middleware and its `autosaveDriven` check.
    if (action?.type === EMBED_CONFIGURATION_CREATE_REQUEST) {
      return !isAutosaveDriven(action);
    }

    return false;
  },
});

const redactedString = 'xxxxxxxxxx';
const sentry = createSentry({
  actionTransformer(action) {
    if (!action || !action.type) return undefined;
    return action.type;
  },
  stateTransformer(state) {
    return state.reduce((newState, value, key) => {
      if (value.isEmpty()) {
        return newState;
      }

      function getStateValue() {
        if (key === 'auth') {
          return value.withMutations(v => {
            if (v.has('token')) {
              v.set('token', redactedString);
            }

            if (v.has('username')) {
              v.set('username', redactedString);
            }
            return v;
          });
        }

        if (key === 'oauth' && value.hasIn(['getty', 'token'])) {
          return value.setIn(['getty', 'token'], redactedString);
        }

        if (key === 'oauth' && value.hasIn(['thinkstock', 'token'])) {
          return value.setIn(['thinkstock', 'token'], redactedString);
        }

        if (key === 'imageEdit' && value.has('dataUrl')) {
          return value.delete('dataUrl');
        }

        if (key === 'asyncAudioClipper') {
          return value.delete('waveform');
        }

        return value;
      }

      return {
        ...newState,
        [key]: getStateValue().toJS(),
      };
    }, {});
  },
  filterErrors: isReportableError,
});

const api = createApi({
  userIdSelector,
  tokenSelector,
  userRegisteredSelector,
  onInvalidToken: () => dispatch => dispatch(logout(false)),
});

function getMixpanelMiddleware(mixpanel) {
  return new MixpanelMiddleware(mixpanel);
}

function getProdMiddleware(mixpanel) {
  return applyMiddleware(
    persistPurge,
    api,
    getMixpanelMiddleware(mixpanel),
    sentry,
    delay,
    thunk,
    modalMiddleware(),
    autosave,
    embedPreviewSync,
    router,
  );
}

function getDevMiddleware(mixpanel) {
  const logger = createLogger({
    stateTransformer: state => {
      if (Immutable.Iterable.isIterable(state)) {
        return state.toJS();
      }
      return state;
    },
    actionTransformer: action => {
      const { payload, ...rest } = action;
      if (!payload) return action;

      const jsPayload = Object.keys(payload).reduce((acc, key) => {
        const value = payload[key];
        return {
          ...acc,
          [key]: Immutable.Iterable.isIterable(value) ? value.toJS() : value,
        };
      }, {});

      return {
        ...rest,
        payload: jsPayload,
      };
    },
  });

  return applyMiddleware(
    persistPurge,
    api,
    getMixpanelMiddleware(mixpanel),
    sentry,
    delay,
    thunk,
    modalMiddleware(),
    autosave,
    embedPreviewSync,
    logger,
    router,
  );
}

export default process.env.NODE_ENV === 'production'
  ? getProdMiddleware
  : getDevMiddleware;
